import { MutableRefObject, useCallback } from 'react';
import { DailySunlightResult } from '@/types/sunlight';
import { Scene } from 'three';

import styles from './styles.module.scss';
import { WeaveAccordion } from '../UI/WeaveAccordion/WeaveAccordion';
import { WorkflowProps } from '@/types/workflow';
import { SunlightList, SunlightListForRegions } from '../SunlightList/SunlightList';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { groupBy } from '@/utils/general';
import { ViewAnalysisStats } from '../ViewAnalysisStats/ViewAnalysisStats';

export interface ControlPanelProps {
	latitude: number | null;
	setLatitude: (lat: number | null) => void;
	longitude: number | null;
	setLongitude: (lon: number | null) => void;
	sceneRef: MutableRefObject<Scene | null>;
	selectedBuildingId: string | null;
	workflowProps: WorkflowProps;
}

export const ControlPanel = ({
	latitude,
	longitude,
	sceneRef,
	selectedBuildingId,
	workflowProps,
}: ControlPanelProps) => {
	const {
		controlsRow,
		viewStatsContainer,
		viewStatsInfo,
		sunlightScore,
		divider,
	} = styles;
	const { sunlightResultsRooms, sunlightResultsGridPoints } = useDailySunlightStore();

	const groupedResults = useCallback(
		() => groupBy<DailySunlightResult>(sunlightResultsRooms || [], 'roomName'),
		[sunlightResultsRooms]
	);

	const detailedGroupedResults = useCallback(
		() =>
			sunlightResultsGridPoints?.map((results) =>
				groupBy<DailySunlightResult>(results, 'roomName')
			),
		[sunlightResultsGridPoints]
	);

	if (!latitude || !longitude || !sceneRef.current) {
		return null;
	}

	return (
		<div>
			<div>
				<div className={viewStatsContainer}>
					<WeaveAccordion label="Sunlight Score" indicatorposition='right'>
						<div className={`${controlsRow} ${sunlightScore}`}>
							<SunlightList
								data={groupedResults()}
								scene={sceneRef.current}
								selectedBuildingId={selectedBuildingId}
								workflowProps={workflowProps}
							/>
							{
								detailedGroupedResults() &&
								detailedGroupedResults()!.length > 0 &&
								<div className={divider} />
							}
							<SunlightListForRegions
								data={detailedGroupedResults()}
								scene={sceneRef.current}
								selectedBuildingId={selectedBuildingId}
								workflowProps={workflowProps}
							/>
						</div>
					</WeaveAccordion>
				</div>
				<div className={viewStatsContainer}>
					<WeaveAccordion label="View Score" indicatorposition='right'>
						<div className={controlsRow}>
							<div className={viewStatsInfo}>
								<ViewAnalysisStats
									workflowProps={workflowProps}
									sceneRef={sceneRef}
									selectedBuildingId={selectedBuildingId}
								/>
							</div>
						</div>
					</WeaveAccordion>
				</div>
			</div>
		</div>
	);
};
